import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader,
  AlertCircle,
  ShieldCheck,
  Users,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Search,
  UserPlus,
  X,
  Import,
  ExternalLink,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { eventTeamApiService } from '../../../services/eventTeamApi';
import { usernameCacheService } from '../../../services/usernameCache';
import type {
  EventCommandCheck,
  EventItem,
  EventPlayerTeam,
  EventPlayerTeamMember,
} from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { CheckTeamModal } from '../CheckTeamModal';
import { CommandCheckModal, parseCheckCommands } from '../CommandCheckModal';
import { PlayerSearchField, type PlayerSelection } from '../PlayerSearchField';
import { ConfirmationModal } from '../../ConfirmationModal';
import { PlayerProfile } from '../../PlayerProfile';
import { useDebounce } from '../../../hooks/useDebounce';
import { cn } from '../../../utils/cn';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';
import { QuickCommandSearch } from '../QuickCommandSearch';

const PAGE_SIZE = 50;

const CHECK_STATUS_CHIP: Record<string, string> = {
  draft: 'bg-glass-200/10 text-glass-400 border-glass-200/20',
  ready: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/25',
  completed: 'bg-green-500/15 text-green-300 border-green-500/25',
};

export interface EventClearancePanelProps {
  perms: EventTeamPerms;
  onOpenCheck: (id: string) => void;
  tourQuickSearch?: boolean;
}

/**
 * Command-Log Clearance (guide §3): build player teams, define command checks,
 * then run them from the check detail screen. "Did anyone on these teams use
 * forbidden commands during the event?"
 */
export const EventClearancePanel = ({ perms, onOpenCheck, tourQuickSearch = false }: EventClearancePanelProps) => {
  const [subTab, setSubTab] = useState<'search' | 'checks' | 'teams'>(perms.canLead ? 'search' : 'checks');
  const beforeTour = useRef<typeof subTab | null>(null);
  useEffect(() => {
    if (tourQuickSearch && perms.canLead) {
      if (beforeTour.current === null) beforeTour.current = subTab;
      if (subTab !== 'search') setSubTab('search');
    } else if (beforeTour.current !== null) {
      setSubTab(beforeTour.current);
      beforeTour.current = null;
    }
  }, [tourQuickSearch, perms.canLead, subTab]);

  // ── Teams state ─────────────────────────────────────────────────────────
  const [teams, setTeams] = useState<EventPlayerTeam[]>([]);
  const [teamsTotal, setTeamsTotal] = useState(0);
  const [teamsPage, setTeamsPage] = useState(1);
  const [teamSearch, setTeamSearch] = useState('');
  const debouncedTeamSearch = useDebounce(teamSearch, 300);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<EventPlayerTeamMember[] | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [newMember, setNewMember] = useState<PlayerSelection | null>(null);
  const [memberBusy, setMemberBusy] = useState(false);

  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState<EventPlayerTeam | null>(null);
  const [teamToDelete, setTeamToDelete] = useState<EventPlayerTeam | null>(null);
  const [deletingTeam, setDeletingTeam] = useState(false);

  // Import participants
  const [importTargetTeam, setImportTargetTeam] = useState<EventPlayerTeam | null>(null);
  const [importEventSearch, setImportEventSearch] = useState('');
  const debouncedImportSearch = useDebounce(importEventSearch, 300);
  const [importEventResults, setImportEventResults] = useState<EventItem[]>([]);
  const [importing, setImporting] = useState(false);

  // ── Checks state ────────────────────────────────────────────────────────
  const [checks, setChecks] = useState<EventCommandCheck[]>([]);
  const [checksTotal, setChecksTotal] = useState(0);
  const [checksPage, setChecksPage] = useState(1);
  const [checkStatusFilter, setCheckStatusFilter] = useState('');
  const [checksError, setChecksError] = useState<string | null>(null);
  const [showCreateCheck, setShowCreateCheck] = useState(false);
  const [editingCheck, setEditingCheck] = useState<EventCommandCheck | null>(null);
  const [checkToDelete, setCheckToDelete] = useState<EventCommandCheck | null>(null);
  const [deletingCheck, setDeletingCheck] = useState(false);

  const [loading, setLoading] = useState(false);

  // ── Loaders ─────────────────────────────────────────────────────────────

  const loadTeams = useCallback(
    async (page: number) => {
      setLoading(true);
      setTeamsError(null);
      try {
        const res = await eventTeamApiService.listCheckTeams({
          search: debouncedTeamSearch.trim() || undefined,
          page,
          limit: PAGE_SIZE,
        });
        setTeams(res.results);
        setTeamsTotal(res.total);
        setTeamsPage(page);
      } catch (err) {
        setTeamsError(err instanceof Error ? err.message : 'Failed to load teams');
      } finally {
        setLoading(false);
      }
    },
    [debouncedTeamSearch],
  );

  const loadChecks = useCallback(
    async (page: number) => {
      setLoading(true);
      setChecksError(null);
      try {
        const res = await eventTeamApiService.listChecks({
          status: checkStatusFilter || undefined,
          page,
          limit: PAGE_SIZE,
        });
        setChecks(res.results);
        setChecksTotal(res.total);
        setChecksPage(page);
      } catch (err) {
        setChecksError(err instanceof Error ? err.message : 'Failed to load checks');
      } finally {
        setLoading(false);
      }
    },
    [checkStatusFilter],
  );

  useEffect(() => {
    if (subTab === 'teams') void loadTeams(1);
    else if (subTab === 'checks') void loadChecks(1);
  }, [subTab, loadTeams, loadChecks]);

  useEffect(() => {
    if (!importTargetTeam || debouncedImportSearch.trim().length === 0) {
      setImportEventResults([]);
      return;
    }
    let cancelled = false;
    eventTeamApiService
      .searchEvents(debouncedImportSearch)
      .then((res) => {
        if (!cancelled) setImportEventResults(res);
      })
      .catch(() => {
        if (!cancelled) setImportEventResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedImportSearch, importTargetTeam]);

  // ── Team member actions ─────────────────────────────────────────────────

  const toggleTeamExpand = async (team: EventPlayerTeam) => {
    if (expandedTeamId === team.id) {
      setExpandedTeamId(null);
      setTeamMembers(null);
      return;
    }
    setExpandedTeamId(team.id);
    setTeamMembers(null);
    setMembersLoading(true);
    try {
      const members = await eventTeamApiService.listCheckTeamMembers(team.id);
      setTeamMembers(members);
      usernameCacheService.prefetchUsernames(members.map((m) => m.user_uuid));
    } catch {
      setTeamMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const refreshExpandedMembers = async () => {
    if (!expandedTeamId) return;
    try {
      const members = await eventTeamApiService.listCheckTeamMembers(expandedTeamId);
      setTeamMembers(members);
    } catch {
      /* keep the current list */
    }
  };

  const handleAddMember = async () => {
    if (!expandedTeamId || !newMember) return;
    setMemberBusy(true);
    try {
      await eventTeamApiService.addCheckTeamMember(expandedTeamId, {
        user_uuid: newMember.uuid,
        display_name: newMember.name,
      });
      setNewMember(null);
      await refreshExpandedMembers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add player');
    } finally {
      setMemberBusy(false);
    }
  };

  const handleRemoveMember = async (uuid: string) => {
    if (!expandedTeamId) return;
    setMemberBusy(true);
    try {
      await eventTeamApiService.removeCheckTeamMember(expandedTeamId, uuid);
      await refreshExpandedMembers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove player');
    } finally {
      setMemberBusy(false);
    }
  };

  const handleImport = async (eventId: string) => {
    if (!importTargetTeam) return;
    setImporting(true);
    try {
      const res = await eventTeamApiService.importParticipantsToTeam(importTargetTeam.id, eventId);
      alert(`Imported ${res.imported} participant${res.imported === 1 ? '' : 's'} into "${importTargetTeam.name}".`);
      setImportTargetTeam(null);
      setImportEventSearch('');
      if (expandedTeamId === importTargetTeam.id) await refreshExpandedMembers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to import participants');
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;
    setDeletingTeam(true);
    try {
      await eventTeamApiService.deleteCheckTeam(teamToDelete.id);
      setTeamToDelete(null);
      if (expandedTeamId === teamToDelete.id) {
        setExpandedTeamId(null);
        setTeamMembers(null);
      }
      await loadTeams(teamsPage);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete team');
    } finally {
      setDeletingTeam(false);
    }
  };

  const handleDeleteCheck = async () => {
    if (!checkToDelete) return;
    setDeletingCheck(true);
    try {
      await eventTeamApiService.deleteCheck(checkToDelete.id);
      setCheckToDelete(null);
      await loadChecks(checksPage);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete check');
    } finally {
      setDeletingCheck(false);
    }
  };

  const renderPager = (page: number, total: number, onPage: (p: number) => void) => {
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1 || loading}
          className="btn-icon"
          title="Previous page"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-glass-400 tabular-nums">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages || loading}
          className="btn-icon"
          title="Next page"
          aria-label="Next page"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Command activity"
        icon={ShieldCheck}
        iconClassName="text-green-400"
        description="Check player activity now, or revisit a saved check."
        className="mb-0 flex-wrap"
        actions={
          <div data-tour="event-command-views" className="segmented flex-wrap max-w-full" role="tablist" aria-label="Clearance views">
            {perms.canLead && <button type="button" role="tab" aria-selected={subTab === 'search'} onClick={() => setSubTab('search')} className="segmented-item" data-active={subTab === 'search' ? 'true' : 'false'}><Search className="w-4 h-4" />Quick search</button>}
            <button
              type="button"
              role="tab"
              aria-selected={subTab === 'checks'}
              onClick={() => setSubTab('checks')}
              className="segmented-item"
              data-active={subTab === 'checks' ? 'true' : 'false'}
            >
              <ShieldCheck className="w-4 h-4" />
              Saved checks
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={subTab === 'teams'}
              onClick={() => setSubTab('teams')}
              className="segmented-item"
              data-active={subTab === 'teams' ? 'true' : 'false'}
            >
              <Users className="w-4 h-4" />
              Groups
            </button>
          </div>
        }
      />

      {subTab === 'search' && perms.canLead && <QuickCommandSearch />}

      {loading && subTab !== 'search' && (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="glass-card p-3 space-y-2">
              <SkeletonLoader className="h-4 w-48 max-w-full" />
              <SkeletonLoader className="h-3 w-80 max-w-full" />
            </div>
          ))}
        </div>
      )}

      {/* ── Checks ──────────────────────────────────────────────────────── */}
      {subTab === 'checks' && !loading && (
        <div className="space-y-3">
          {checksError && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span className="flex-1">{checksError}</span>
            </div>
          )}
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <select
              value={checkStatusFilter}
              onChange={(e) => setCheckStatusFilter(e.target.value)}
              className="glass-select md:w-44 capitalize"
              aria-label="Filter checks by status"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="ready">Ready</option>
              <option value="completed">Completed</option>
            </select>
            <span className="text-xs text-glass-400 tabular-nums">{checksTotal} checks</span>
            <div className="flex-1" />
            {perms.canLead && (
              <button type="button" onClick={() => setShowCreateCheck(true)} className="btn-primary">
                <Plus className="w-4 h-4" />
                New check
              </button>
            )}
          </div>

          {checks.length === 0 && !checksError ? (
            <EmptyState
              icon={ShieldCheck}
              title="No command checks"
              description={perms.canLead ? 'Create a check to see whether teams used forbidden commands.' : undefined}
            />
          ) : (
            <div className="space-y-2">
              {checks.map((check) => {
                const commands = parseCheckCommands(check.commands);
                return (
                  <div key={check.id} className="glass-card p-3 md:p-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <button type="button" onClick={() => onOpenCheck(check.id)} className="min-w-0 flex-1 text-left group">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-white group-hover:text-accent-300 transition-colors">
                            {check.title}
                          </span>
                          <span
                            className={cn('badge badge-dot capitalize', CHECK_STATUS_CHIP[check.status] ?? CHECK_STATUS_CHIP.draft)}
                          >
                            {check.status}
                          </span>
                          <ExternalLink className="w-3 h-3 text-glass-500" />
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-glass-500 tabular-nums">
                          <span className="font-mono">
                            {commands.slice(0, 4).join(' ')}
                            {commands.length > 4 && ` +${commands.length - 4}`}
                          </span>
                          <span className="capitalize">({check.match_mode})</span>
                          {check.scope_fqdn && <span className="text-blue-300">{check.scope_fqdn}</span>}
                          {check.scope_cluster && <span className="text-violet-300">cluster {check.scope_cluster}</span>}
                          <span>
                            {format(new Date(check.time_start), 'MMM d HH:mm')} →{' '}
                            {format(new Date(check.time_end), 'MMM d HH:mm')}
                          </span>
                        </div>
                      </button>
                      {perms.canLead && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setEditingCheck(check)}
                            className="btn-icon btn-icon-sm"
                            title="Edit check"
                            aria-label="Edit check"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setCheckToDelete(check)}
                            className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                            title="Delete check (results cascade)"
                            aria-label="Delete check"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {renderPager(checksPage, checksTotal, (p) => void loadChecks(p))}
        </div>
      )}

      {/* ── Teams ───────────────────────────────────────────────────────── */}
      {subTab === 'teams' && !loading && (
        <div className="space-y-3">
          {teamsError && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span className="flex-1">{teamsError}</span>
            </div>
          )}
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                placeholder="Search teams…"
                className="glass-input w-full pl-9"
              />
            </div>
            <span className="text-xs text-glass-400 tabular-nums">{teamsTotal} teams</span>
            {perms.canLead && (
              <button type="button" onClick={() => setShowCreateTeam(true)} className="btn-primary">
                <Plus className="w-4 h-4" />
                New team
              </button>
            )}
          </div>

          {teams.length === 0 && !teamsError ? (
            <EmptyState
              icon={Users}
              title="No player teams"
              description={perms.canLead ? "Create one, or import an event's participants." : undefined}
            />
          ) : (
            <div className="space-y-2">
              {teams.map((team) => {
                const expanded = expandedTeamId === team.id;
                return (
                  <div key={team.id} className="glass-card overflow-visible">
                    <div className="flex items-center justify-between gap-2 p-3 flex-wrap">
                      <button
                        type="button"
                        onClick={() => void toggleTeamExpand(team)}
                        className="flex items-center gap-2 min-w-0 flex-1 text-left"
                        aria-expanded={expanded}
                      >
                        {expanded ? (
                          <ChevronDown className="w-4 h-4 text-glass-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-glass-400 flex-shrink-0" />
                        )}
                        {team.color && (
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }} />
                        )}
                        <span className="text-sm font-semibold text-white truncate">{team.name}</span>
                        {team.event_id && (
                          <span className="badge badge-accent">event-linked</span>
                        )}
                        {team.notes && (
                          <span className="text-[11px] text-glass-500 italic truncate max-w-[220px]">{team.notes}</span>
                        )}
                        <span className="text-[11px] text-glass-500 tabular-nums">
                          {formatDistanceToNow(new Date(team.created_at), { addSuffix: true })}
                        </span>
                      </button>
                      {perms.canLead && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setImportTargetTeam(team)}
                            className="btn-icon btn-icon-sm text-cyan-300"
                            title="Import an event's participants into this team"
                            aria-label="Import participants"
                          >
                            <Import className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingTeam(team)}
                            className="btn-icon btn-icon-sm"
                            title="Edit team"
                            aria-label="Edit team"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setTeamToDelete(team)}
                            className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                            title="Delete team (members + results cascade)"
                            aria-label="Delete team"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {expanded && (
                      <div className="border-t border-glass-200/10 p-3 space-y-2">
                        {membersLoading ? (
                          <Loader className="w-5 h-5 animate-spin text-accent-400" />
                        ) : (
                          <>
                            <p className="eyebrow">
                              Members ({teamMembers?.length ?? 0})
                            </p>
                            {(teamMembers ?? []).length === 0 ? (
                              <p className="text-glass-400 text-sm">No players.</p>
                            ) : (
                              <div className="flex flex-wrap gap-1.5">
                                {(teamMembers ?? []).map((m) => (
                                  <span
                                    key={m.user_uuid}
                                    className="chip gap-1.5 px-2 py-1"
                                  >
                                    <PlayerProfile
                                      uuid={m.user_uuid}
                                      name={m.display_name ?? undefined}
                                      showUuid={false}
                                      inline
                                    />
                                    {perms.canLead && (
                                      <button
                                        onClick={() => void handleRemoveMember(m.user_uuid)}
                                        disabled={memberBusy}
                                        className="text-glass-500 hover:text-red-300 rounded"
                                        title="Remove player"
                                        aria-label="Remove player"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                            {perms.canLead && (
                              <div className="flex items-center gap-2 flex-wrap pt-1">
                                <PlayerSearchField
                                  value={newMember}
                                  onChange={setNewMember}
                                  compact
                                  className="flex-1 min-w-[200px]"
                                  placeholder="Add player by name…"
                                />
                                <button
                                  onClick={() => void handleAddMember()}
                                  disabled={memberBusy || !newMember}
                                  className="glass-button-secondary btn-xs"
                                >
                                  {memberBusy ? <Loader className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                                  Add
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {renderPager(teamsPage, teamsTotal, (p) => void loadTeams(p))}
        </div>
      )}

      {/* ── Import participants modal ───────────────────────────────────── */}
      {importTargetTeam && (
        <div
          className="modal-overlay z-[9990]"
          onClick={() => setImportTargetTeam(null)}
        >
          <div
            className="modal-panel max-w-md"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Import participants"
          >
            <div className="modal-header">
              <h3 className="modal-title inline-flex items-center gap-2">
                <Import className="w-5 h-5 text-cyan-400" />
                Import participants → {importTargetTeam.name}
              </h3>
              <button
                type="button"
                onClick={() => setImportTargetTeam(null)}
                className="modal-close"
                title="Close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={importEventSearch}
                onChange={(e) => setImportEventSearch(e.target.value)}
                placeholder="Search events…"
                className="glass-input w-full pl-9"
                autoFocus
              />
            </div>
            {importEventResults.length > 0 && (
              <div className="space-y-1 max-h-[240px] overflow-y-auto">
                {importEventResults.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => void handleImport(e.id)}
                    disabled={importing}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left hover:bg-glass-200/10 transition-colors"
                  >
                    <span className="text-sm text-white truncate">{e.title}</span>
                    {importing ? (
                      <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" />
                    ) : (
                      <Import className="w-3.5 h-3.5 text-cyan-300 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      <CheckTeamModal
        isOpen={showCreateTeam}
        mode="create"
        onClose={() => setShowCreateTeam(false)}
        onSuccess={() => void loadTeams(1)}
      />
      {editingTeam && (
        <CheckTeamModal
          isOpen={!!editingTeam}
          mode="edit"
          team={editingTeam}
          onClose={() => setEditingTeam(null)}
          onSuccess={() => {
            setEditingTeam(null);
            void loadTeams(teamsPage);
          }}
        />
      )}
      <CommandCheckModal
        isOpen={showCreateCheck}
        mode="create"
        onClose={() => setShowCreateCheck(false)}
        onSuccess={(created) => onOpenCheck(created.id)}
      />
      {editingCheck && (
        <CommandCheckModal
          isOpen={!!editingCheck}
          mode="edit"
          check={editingCheck}
          onClose={() => setEditingCheck(null)}
          onSuccess={() => {
            setEditingCheck(null);
            void loadChecks(checksPage);
          }}
        />
      )}
      {teamToDelete && (
        <ConfirmationModal
          isOpen={!!teamToDelete}
          onClose={() => setTeamToDelete(null)}
          onConfirm={() => void handleDeleteTeam()}
          title="Delete Player Team"
          message={`Delete "${teamToDelete.name}"? Its members and any check results for it cascade.`}
          confirmText="Delete"
          isLoading={deletingTeam}
        />
      )}
      {checkToDelete && (
        <ConfirmationModal
          isOpen={!!checkToDelete}
          onClose={() => setCheckToDelete(null)}
          onConfirm={() => void handleDeleteCheck()}
          title="Delete Command Check"
          message={`Delete "${checkToDelete.title}"? All recorded runs and violations cascade.`}
          confirmText="Delete"
          isLoading={deletingCheck}
        />
      )}
    </div>
  );
};
